/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";

//Local Imports
import { CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { useModals } from "../../store/dialogs/hooks.js";
import { useNoiseFilter } from "../../store/noiseFilter/hooks.js";
import { existsString } from "../../store/validation.js";
import { FineTuneDialog } from "../dialogs";
import AudioControls from "./AudioControls";
import WaveformDisplay from "./WaveformDisplay";
import {
  useEditor,
  useEditorOperations,
  useEditorPreferences,
} from "../../store/editor/hooks.js";
import { useAnnotations } from "../../store/annotations/hooks.js";
import { WaitDialog } from "../../components/dialogs";
import { formatMinutes } from "../../pages/app/utils/formatTime.js";
import { NOT_TAGGED } from "../../pages/app/utils/constants.js";

// regions are a wavesurfer concept, we use them to display annotations
// we must convert as wavesurfer regions use html events and we use redux for state management
let regions = [];
let localTagList = [];
let selectedRegion = null;

const regionToAnnotation = (region) => ({
  id: region.id,
  start: region.start,
  end: region.end,
  tag: region.tag && region.tag !== "" ? region.tag : NOT_TAGGED,
});

const regionActions = {
  add: (region) => {
    const existingRegion = regions.find((r) => r.id === region.id);
    console.log("adding region id " + region.id);
    if (!existingRegion) {
      const newRegion = regionToAnnotation(region);
      regions.push(newRegion);
      return newRegion;
    } else {
      existingRegion.start = region.start;
      existingRegion.end = region.end;
      existingRegion.tag = region.tag;
      return existingRegion;
    }
  },
  update: (region) => {
    const index = regions.findIndex((r) => r.id === region.id);
    if (index !== -1) {
      const tag = regions[index].tag;
      regions[index] = regionToAnnotation(region);
      regions[index].tag = tag; // Preserve the tag
      return regions[index];
    }
    return null;
  },
  remove: (regionId) => {
    regions = regions.filter((r) => r.id !== regionId);
  },
  clear: () => {
    regions = [];
  },
  find: (regionId) => {
    return regions.find((r) => r.id === regionId);
  },
};

const NEW_REGION_COLOR = "hsla(0, 80%, 50%, 0.10)";
const SELECTED_REGION_COLOR = "hsla(50, 100%, 50%, 0.3)";
const REGION_COLORS = [
  "hsla(180, 80%, 75%, 0.2)",
  "hsla(90, 80%, 75%, 0.2)",
  "hsla(210, 80%, 75%, 0.2)",
  "hsla(300, 80%, 75%, 0.2)",
  "hsla(240, 80%, 75%, 0.2)",
  "hsla(120, 80%, 75%, 0.2)",
  "hsla(270, 80%, 75%, 0.2)",
  "hsla(150, 80%, 75%, 0.2)",
  "hsla(0, 80%, 75%, 0.2)",
  "hsla(60, 80%, 75%, 0.2)",
];

const getTagForWaveSurferRegion = (wsr) => {
  const foundRegion = regionActions.find(wsr.id);
  if (foundRegion) return foundRegion.tag;
  return NOT_TAGGED;
};

const getColorForTag = (tag) => {
  if (!existsString(tag) || tag === NOT_TAGGED) {
    return NEW_REGION_COLOR;
  }
  for (let i = 0; i < localTagList.length; i++) {
    if (localTagList[i] === tag) {
      const colorIndex = i % REGION_COLORS.length;
      return REGION_COLORS[colorIndex];
    }
  }
  return NEW_REGION_COLOR;
};

export const WaveSurferPlayer = ({
  barGap,
  children,
  clip,
  mode = "editor",
  preferencesSave,
  readOnly,
  regionsPlugin,
  wavesurfer,
}) => {
  const theme = useTheme();
  const { modalIsOpen, closeFFTSettingsDialog, showExtractDialog } =
    useModals();
  const { updateNoiseFilter, noiseStart, noiseEnd } = useNoiseFilter();
  const mainRef = useRef();
  const clipId = clip.id;
  const caseId = clip.afCase.id;
  const [regionsLoaded, setRegionsLoaded] = useState(false);

  const {
    annotations,
    isSaving,
    tagList,
    tagUpdateNumber,
    addAnnotation,
    cleanUpRegions,
    deleteAnnotation,
    saveAnnotations,
  } = useAnnotations();

  const { setPlaybackTime, selectAnnotation, clearSelection, hasSelection } =
    useEditor();
  const { preferences, updateFFTFrequencies, updatePreferences } =
    useEditorPreferences();
  const { waveOpacity, zoomAmount, fftSamples, fftFrequencies } = preferences;
  const { selectedTag } = useEditorOperations();

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [wavesurferReady, setWavesurferReady] = useState(false);
  const wsRegions = regionsPlugin;
  const sampleRate = clip ? clip.audioFiles[0].sampleRate : 1;
  const duration = Math.floor(clip ? clip.audioFiles[0]?.duration : 1);

  const [audioLoading, setAudioLoading] = useState({ percent: 0, retries: 0 });
  let wsTime = 0;

  // value subtracted from window height percentage is elements above wavesurfer in parent and controls below
  const waveSurferHeight = Math.floor(windowHeight * 0.925 - 290);

  // sticky annotation
  const [visibleAudio, setVisibleAudio] = useState({ start: 0, end: 0 });
  const [isDragging, setIsDragging] = useState(false);

  //We have to get the width dynamically in order for the overlapping windows to render correctly
  useEffect(() => {
    const load = async () => {
      const freqMax = Math.min(fftFrequencies.max, sampleRate / 2.0); // if there are no preferences, it defaults to 20000 which may be way higher than the possible range given the sample rate
      if (freqMax < fftFrequencies.max) {
        updateFFTFrequencies({
          min: fftFrequencies.min,
          max: freqMax,
        });
      }
      setupRegionDefaults();

      const handleWindowResize = () => {
        setWindowHeight(window.innerHeight);
        setWindowWidth(window.innerWidth);
      };
      window.addEventListener("resize", handleWindowResize);
      return () => {
        window.removeEventListener("resize", handleWindowResize);
      };
    };

    load();
  }, []);

  const setFrequencyRange = (minimum, maximum) => {
    const min = Math.max(minimum, 0);
    const max = Math.min(maximum, sampleRate / 2.0);
    const options = { fftFrequencies: { min, max } };
    updatePreferences(options);
    preferencesSave(options);
  };

  const handleSelectAnnotation = (region) => {
    if (selectedRegion !== region?.id) {
      selectedRegion = region.id;
      selectAnnotation(region.id, region.tag);
    }
  };

  const handleClearSelection = () => {
    selectedRegion = null;
    clearSelection();
  };

  useEffect(() => {
    if (mode === "sng") {
      const region = regionActions.find("sng");
      if (region?.start !== noiseStart || region?.end !== noiseEnd) {
        regionActions.update({ ...region, start: noiseStart, end: noiseEnd });
        drawRegions("noise filter update");
      }
    }
  }, [noiseStart, noiseEnd]);

  const setupEventHandlers = () => {
    if (!wavesurfer) return;

    const regionCreated = (region) => {
      const r = regionActions.find(region.id);
      const regionExists = r !== undefined;
      const regionTag = r?.tag;
      if (!regionExists) {
        region.tag = region.tag || NOT_TAGGED;
        addAnnotation(regionToAnnotation(region));
        handleSelectAnnotation(region);
        //clearSelection();
        regionActions.add(region);
        regions = cleanUpRegions(regions);
        region.setOptions({
          color: NEW_REGION_COLOR,
        });
        drawRegions("regionCreated");
      } else {
        region.setOptions({
          color: getColorForTag(regionTag),
        });
      }
      console.log("Created region", region);
      highlightRegions("regionCreated");

      // sticky annotation
      region.on("update", () => {
        setIsDragging(true);
      });
      region.on("update-end", () => {
        setIsDragging(false);
      });
    };

    const regionUpdated = async (region) => {
      console.log("Updated region", region);
      if (mode === "sng" || mode === "sync") {
        if (region.id === "sng") {
          updateNoiseFilter({
            noiseStart: parseFloat(region.start.toFixed(3)),
            noiseEnd: parseFloat(region.end.toFixed(3)),
          });
        }
        return;
      } else {
        const updatedRegion = regionActions.update(region);
        handleSelectAnnotation(updatedRegion);
        regions = cleanUpRegions(regions);
        saveAnnotations(
          caseId,
          clipId,
          regions,
          updatedRegion.tag === NOT_TAGGED,
        );
        drawRegions("regionUpdated");
      }
    };

    const regionClicked = (r) => {
      const region = regions.find((region) => region.id === r.id);
      if (!region) {
        return;
      }
      handleSelectAnnotation(region);
      highlightRegions("regionClicked");
    };

    // sticky annotation
    const waveSurferScroll = (visibleStartTime, visibleEndTime) =>
      setVisibleAudio({ start: visibleStartTime, end: visibleEndTime });

    const wavesurferClicked = (x, y) => {
      const currentTime = wavesurfer.getCurrentTime();
      let found = false;
      const allRegions = wsRegions.getRegions();
      for (let r of regions) {
        console.log("checking region", r, currentTime);
        if (currentTime >= r.start && currentTime <= r.end) {
          found = true;
          break;
        }
      }
      if (!found) {
        handleClearSelection();
        highlightRegions("wavesurferClicked");
      }
    };

    let decode = () => {};
    // if (mode === "sng" || mode === "sync") {
    //   decode = () => {
    //     const sng_region = { id: "sng", start: 0, end: 2, tag: NOT_TAGGED };
    //     wsRegions.clearRegions();
    //     drawRegion(null, null, null, wsRegions, sng_region, true);
    //     handleSelectAnnotation(sng_region);
    //   };
    // }

    const handleZoom = () => {
      // Re-calculate the visible time after zooming
      const visiblePixels = mainRef.current.clientWidth;
      const scrollPixels = wavesurfer.getScroll();
      const minPxPerSec = wavesurfer.options.minPxPerSec || 0;

      if (minPxPerSec > 0) {
        const visibleStart = scrollPixels / minPxPerSec;
        const visibleEnd = (scrollPixels + visiblePixels) / minPxPerSec;
        setVisibleAudio({ start: visibleStart, end: visibleEnd });
      }
    };

    wavesurfer.on("decode", decode);
    wsRegions.on("region-created", regionCreated);
    wsRegions.on("region-updated", regionUpdated);
    wsRegions.on("region-clicked", regionClicked);
    wavesurfer.on("click", wavesurferClicked);
    // sticky annotation
    wavesurfer.on("scroll", (visibleStartTime, visibleEndTime) => {
      waveSurferScroll(visibleStartTime, visibleEndTime);
    });
    wavesurfer.on("zoom", handleZoom);

    return () => {
      wavesurfer.un("decode", decode);
      wsRegions.un("region-created", regionCreated);
      wsRegions.un("region-updated", regionUpdated);
      wsRegions.un("region-clicked", regionClicked);
      wavesurfer.un("click", wavesurferClicked);
      // sticky annotation
      wavesurfer.un("scroll", (visibleStartTime, visibleEndTime) => {
        waveSurferScroll(visibleStartTime, visibleEndTime);
      });
      wavesurfer.un("zoom", handleZoom);
    };
  };

  //Time update effect
  useEffect(() => {
    if (!wavesurfer) return;

    const timeUpdate = async () => {
      const currentTime = wavesurfer.getCurrentTime();
      setPlaybackTime(currentTime, clip);

      //Check to see if there is a selected tag.
      const selectedRegions = [];

      for (let r of regions) {
        if (hasSelection && r.tag === selectedTag) {
          selectedRegions.push(r);
        }
      }

      // When the end of the region is reached
      if (selectedRegions.length > 0 && wavesurfer.isPlaying()) {
        let skip = false;
        let skipTime = undefined;
        for (let i = 0; i < selectedRegions.length - 1; i++) {
          if (
            currentTime >= selectedRegions[i].end &&
            currentTime < selectedRegions[i + 1].start
          ) {
            console.log(
              ">>>>>>>NEXT REGION START: " + currentTime + " index:" + (i + 1),
              selectedRegions,
            );
            skipTime = selectedRegions[i + 1].start;
            skip = true;
            break;
          }
        }

        // Loop back to start of first selection.
        if (skipTime === undefined && currentTime < selectedRegions[0].start) {
          console.log(
            ">>>>>>>BACK TO FIRST SELECTION x2: " + currentTime,
            selectedRegions,
          );
          skipTime = selectedRegions[0].start;
          skip = true;
        } else if (
          skipTime === undefined &&
          currentTime >= selectedRegions[selectedRegions.length - 1].end
        ) {
          console.log(
            ">>>>>>>BACK TO FIRST SELECTION: " + currentTime,
            selectedRegions,
          );
          skipTime = selectedRegions[0].start;
          skip = true;
        }

        if (skip) {
          wavesurfer.setTime(skipTime);
        }
      }
    };

    wavesurfer.on("timeupdate", timeUpdate);

    return () => {
      wavesurfer.un("timeupdate", timeUpdate);
    };
  }, [wavesurfer, selectedTag, hasSelection, windowWidth]);

  const handleRemoveAnnotation = async (caseId, clipId, annotationId) => {
    console.log("[WavesurferPlayer] Removing annotation", annotationId);
    if (selectedRegion === annotationId) {
      handleClearSelection(); // remove selection if the annotation was selected
    }
    regionActions.remove(annotationId); // remove local
    await deleteAnnotation(caseId, clipId, annotationId); // remove redux version
    drawRegions("handleRemoveAnnotation"); // get wavesurfer synced again
    highlightRegions("handleRemoveAnnotation");
  };

  // sticky annotation
  const calcVisibleRegionWidth = (region, visibleAudio) =>
    region.start < visibleAudio.start
      ? (
          ((region.end - visibleAudio.start) / (region.end - region.start)) *
          100
        ).toFixed(2)
      : "100";

  const drawRegion = (
    deleteRegionById,
    caseId,
    clipId,
    regionsPlugin,
    region,
    readOnly,
  ) => {
    const content = document.createElement(region.id);

    content.innerHTML = `
                <div style="background:rgb(255 255 255 / 65%);margin-bottom:2px;padding:2px 8px;font-size:11px;line-height:1;font-weight:500;color:rgb(0 0 0 / 87%);display:none;justify-content:center;align-items:center;min-width:fit-content;max-width:100%;">${formatMinutes(region.end - region.start)}</div>
                <div part="region-content" style="min-height:36px;display:flex;min-width:fit-content;max-width:100%;justify-content:flex-end;align-items:center;
                ${
                  region.id === "sng"
                    ? ""
                    : "background:rgb(255 255 255 / 65%);margin-bottom:20px;box-shadow:rgba(0, 0, 0, 0.2) 0px 3px 3px -2px, rgba(0, 0, 0, 0.14) 0px 3px 4px 0px, rgba(0, 0, 0, 0.12) 0px 1px 8px 0px;"
                }">
                  <div style="display:flex;align-items:center;justify-content:space-between;width:${calcVisibleRegionWidth(
                    region,
                    visibleAudio,
                  )}%;min-width:fit-content;max-width:100%;align-self:flex-end;">
                    <div style="padding:8px;font-size:12px;font-weight:500;color:rgb(0 0 0 / 87%)">
                    ${region.tag === NOT_TAGGED ? "" : region.tag}
                    </div>
                    ${
                      readOnly || region.id === "sng"
                        ? ""
                        : `<button 
                            onmouseover="style.opacity=.5" 
                            onmouseout="style.opacity=1" 
                            style="display:flex;justify-content:center;align-items:center;
                            cursor:pointer;background-color:transparent;border:0;height:32px;width:32px;
                            transition: opacity .250s ease-in-out" 
                            id="${region.id}">
                              <svg xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24" fill="rgb(0 0 0 / 87%)"
                              height="18px" width="18px">
                              <path d="M0 0h24v24H0z" fill="none" />
                              <path d="M14.59 8L12 10.59 9.41 8 8 9.41 10.59 12 8 14.59 9.41 16 12 13.41 14.59 16 16 14.59 13.41 12 16 9.41 14.59 8zM12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" />
                              </svg>
                            </button>`
                    }
                  </div>
                    </div>`;

    regionsPlugin.addRegion({
      start: region.start,
      end: region.end,
      id: region.id,
      color: getColorForTag(region.tag),
      content: content,
      drag: !readOnly,
      resize: !readOnly,
    });

    if (readOnly) return;
    content.addEventListener("click", () => {
      if (region.id) {
        // newRegion.remove();
        deleteRegionById(caseId, clipId, region.id);
      }
    });
  };

  const drawRegions = async (caller = "unspecified") => {
    if (!wsRegions) return;
    await wsRegions.clearRegions();
    // console.log("[DRAW REGIONS] Drawing regions", regions, caller);
    for (let r of regions) {
      drawRegion(
        handleRemoveAnnotation,
        caseId,
        clipId,
        wsRegions,
        r,
        readOnly,
      );
    }
  };

  const setupRegionDefaults = () => {
    if (regionsLoaded || !wsRegions) return;
    setRegionsLoaded(true);
    regionActions.clear(); // clear regions array
    if (!readOnly) {
      if (mode !== "sng" && mode !== "sync") {
        wsRegions.enableDragSelection({
          color: "rgba(255, 0, 0, 0.1)",
          content: "",
        });
      }
    }
    for (let r of annotations) {
      regionActions.add(r);
      console.log("setupRegionDefaults adding region", r);
    }
    drawRegions("setupRegionDefaults");
    highlightRegions("setupRegionDefaults");
  };

  useEffect(() => {
    regionActions.clear();
    for (let r of annotations) {
      regionActions.add(r);
    }
    localTagList = [...tagList];
    handleClearSelection();
    drawRegions("useEffect tagUpdateNumber");
    highlightRegions("useEffect tagUpdateNumber");
  }, [tagUpdateNumber]);

  useEffect(() => {
    if (!wavesurfer) return;
    // setup wavesurfer

    wavesurfer.setOptions({
      ...wavesurfer.options,
      sampleRate: sampleRate,
      hideScrollbar: false,
      barGap: barGap,
    });

    setupEventHandlers();
    setupRegionDefaults();
    setSpectrogramRange(fftFrequencies);

    const wsReady = (e) => {
      console.log("ON AUDIO READY", e);
      setWavesurferReady(true);
      wavesurfer.setTime(wsTime);
    };

    const wsDecode = () => {
      wavesurfer.setOptions({
        ...wavesurfer.options,
      });
      setWavesurferZoom(zoomAmount, duration, mainRef.current.scrollWidth);
    };

    const wsLoading = (percent, retries) => {
      setAudioLoading({ percent, retries });
    };

    const wsLoad = () => {
      setWavesurferReady(false);
      wsTime = wavesurfer.getCurrentTime();
    };
    wavesurfer.on("ready", wsReady);
    wavesurfer.on("decode", wsDecode);
    wavesurfer.on("loading", wsLoading);
    wavesurfer.on("load", wsLoad);

    return () => {
      wavesurfer.un("ready", wsReady);
      wavesurfer.un("decode", wsDecode);
      wavesurfer.un("loading", wsLoading);
      wavesurfer.un("load", wsLoad);
    };
  }, [wavesurfer]);

  const updateSelectedRegionDisplay = (regionId) => {
    if (!wsRegions) return;
    const wsregions = wsRegions.getRegions();
    const region = wsregions.find((r) => r.id === regionId);
    if (!region || !region.content) return;

    const durationDiv = region.content.querySelector("div");
    if (durationDiv) {
      const isSelected =
        regionId === selectedRegion &&
        regionActions.find(regionId)?.tag ===
          regionActions.find(selectedRegion)?.tag
          ? "flex"
          : "none";
      durationDiv.style.display = isSelected;
    }
  };

  const highlightRegions = (caller = "unspecified") => {
    if (!wsRegions) return;
    const wsregions = wsRegions.getRegions();
    const selectedTag = regionActions.find(selectedRegion)?.tag;
    for (let r of wsregions) {
      //Change the color of the regions
      const tag = getTagForWaveSurferRegion(r);
      console.log("highlightRegions", r.id, tag, selectedTag, caller);
      if (tag === selectedTag) {
        r.setOptions({ color: SELECTED_REGION_COLOR });
      } else {
        r.setOptions({ color: getColorForTag(tag) });
      }
      updateSelectedRegionDisplay(r.id);
    }
  };

  const getSpectrogramPlugin = (wavesurfer) => {
    if (wavesurfer) {
      const plugins = wavesurfer.getActivePlugins();
      console.log("PLUGINS: ", plugins);
      for (let plugin of plugins) {
        if (
          plugin &&
          plugin.fftSamples !== undefined &&
          plugin.frequencyMax !== undefined
        ) {
          return plugin;
        }
      }
    }
    return undefined;
  };

  useEffect(() => {
    setWaveSampleRate(wavesurfer);
    setSpectrogramOpacity(wavesurfer, waveOpacity);
  }, [wavesurfer, waveOpacity]);

  useEffect(() => {
    drawRegions("useEffect wavesurferReady");
    if (wavesurferReady) {
      if (zoomAmount) {
        wavesurfer.zoom(zoomAmount);
      }
    }
  }, [wavesurferReady]);

  const setSpectrogramOpacity = (wavesurfer, waveOpacity) => {
    const plugin = getSpectrogramPlugin(wavesurfer);
    if (plugin !== undefined) {
      let o = 1 - waveOpacity;
      if (o > 0.99) o = 0.99; //Hack to fix region overlay
      plugin.setOpacity(o);
    }
  };

  const setWaveSampleRate = (wavesurfer) => {
    if (wavesurfer) {
      wavesurfer.setOptions({
        ...wavesurfer.options,
        sampleRate: sampleRate,
        hideScrollbar: false,
      });
    }
  };

  useEffect(() => {
    const spaceBar = (event) => {
      console.log("SPACEBAR", modalIsOpen);
      if (!modalIsOpen) {
        if (event.code === "Space") {
          event.preventDefault();
          if (wavesurfer) {
            wavesurfer.playPause();
          }
        }
      }
    };

    document.addEventListener("keydown", spaceBar);
    return () => {
      document.removeEventListener("keydown", spaceBar);
    };
  }, [wavesurfer, modalIsOpen]);

  const reRender = () => {
    console.log("rerendering");
    wavesurfer.setOptions({ height: waveSurferHeight });
    const spectroPlugin = getSpectrogramPlugin(wavesurfer);
    spectroPlugin?.updateHeight(waveSurferHeight);
    spectroPlugin?.render();
  };

  const fftCancel = () => {
    closeFFTSettingsDialog();
  };

  const fftApply = (fftZoom, fftSize) => {
    closeFFTSettingsDialog();
    const options = {
      fftSamples: fftSize,
      fftFrequencies: { min: fftZoom[0], max: fftZoom[1] },
    };
    updatePreferences(options);
    preferencesSave(options);
  };

  const setSpectrogramRange = (fftFrequencies) => {
    const plugin = getSpectrogramPlugin(wavesurfer);
    if (plugin !== undefined) {
      plugin.frequencyMin = fftFrequencies.min;
      plugin.frequencyMax = fftFrequencies.max;
      reRender();
    }
  };

  useEffect(() => {
    setSpectrogramRange(fftFrequencies);
  }, [fftFrequencies.min, fftFrequencies.max]);

  useEffect(() => {
    const plugin = getSpectrogramPlugin(wavesurfer);

    if (plugin) {
      plugin.fftSamples = fftSamples;
      plugin.frequencies = [];
      reRender();
    }
  }, [fftSamples]);

  useEffect(() => {
    if (wavesurferReady) {
      setWavesurferZoom(zoomAmount, duration, mainRef.current.scrollWidth);
      reRender();
    }
  }, [zoomAmount, wavesurferReady, windowHeight, windowWidth]);

  const setWavesurferZoom = (value, duration, windowWidth) => {
    const secondsPerScreenMin = duration / 100; // max number of screens
    const multiplier = Math.max(1.5, secondsPerScreenMin);
    windowWidth = windowWidth - (32 + 150); // scroll bar, rulers
    let maxZoom = Math.floor(windowWidth / multiplier); // minimum number of seconds on the screen at once
    if (maxZoom > 500) {
      // TODO: Decide how many pixels per inch we are building to, set this to 1/2 that value
      maxZoom = 500;
    }
    let minZoom = Math.floor(windowWidth / duration);
    if (minZoom >= 1) minZoom--;
    if (value === 0) {
      wavesurfer.zoom(minZoom - 10);
      return;
    }

    minZoom = Math.max(0.1, minZoom);
    const logmin = Math.log(minZoom);
    const logmax = Math.log(maxZoom);
    const steps = (logmax - logmin) / 100;
    const minPxPerSec = Math.exp(logmin + value * steps);
    wavesurfer.zoom(minPxPerSec);
  };

  //sort the regions based on their starting timestamp to
  //ensure they will iterate in order
  const sortRegionsByTimestamp = (regions) => {
    return regions
      .map((r) => regionToAnnotation(r))
      .sort((a, b) => a.start - b.start);
  };

  const onNext = () => {
    const currentTime = wavesurfer.getCurrentTime();
    //get the regions in order
    let sortedRegions = sortRegionsByTimestamp(regions);
    const selectedTag = regionActions.find(selectedRegion)?.tag || null;

    if (selectedTag) {
      sortedRegions = sortedRegions.filter((r) => r.tag === selectedTag);
    }
    sortedRegions = sortedRegions.filter((r) => r.start > currentTime);
    if (sortedRegions.length === 0) {
      // go to end of audio
      wavesurfer.setTime(wavesurfer.getDuration());
      return;
    }
    wavesurfer.setTime(sortedRegions[0].start);
    return;
  };

  const onPrevious = () => {
    const currentTime = wavesurfer.getCurrentTime();
    //get the regions in order
    let sortedRegions = sortRegionsByTimestamp(regions);
    const selectedTag = regionActions.find(selectedRegion)?.tag || null;

    if (selectedTag) {
      sortedRegions = sortedRegions.filter((r) => r.tag === selectedTag);
    }
    sortedRegions = sortedRegions.filter((r) => r.start < currentTime);
    if (sortedRegions.length === 0) {
      // go to start of audio
      wavesurfer.setTime(0);
      return;
    }
    wavesurfer.setTime(sortedRegions[sortedRegions.length - 1].start);
    return;
  };

  const audioLoadingDisplay = () => {
    if (audioLoading.percent === 100) {
      return <Typography variant="h6">Developing Spectrogram</Typography>;
    }
    return (
      <Typography variant="h6">
        Audio Loading: {audioLoading.percent}%
      </Typography>
    );
  };

  // sticky annotation
  useEffect(() => {
    if (!isDragging && regions.length === wsRegions?.getRegions().length) {
      drawRegions("visibleAudio changed");
    }
  }, [visibleAudio]);

  // update selected region when user selects a tag from the create model dialog
  useEffect(() => {
    if (showExtractDialog) {
      const dropdownSelectedRegion = regions.find(
        (item) => item.tag === selectedTag,
      );
      handleSelectAnnotation(dropdownSelectedRegion);
      highlightRegions("selectedTag changed");
      wavesurfer.setTime(dropdownSelectedRegion.start);
    }
  }, [selectedTag]);

  return (
    <Stack height={1} position="relative">
      <Stack
        alignItems="center"
        justifyContent="center"
        position="absolute"
        height={1}
        width={1}
        gap={5}
        backgroundColor={`${theme.palette.background.default}`}
        zIndex={100}
        sx={{
          opacity: wavesurferReady ? 0 : 1,
          pointerEvents: "none",
          top: -50,
        }}
      >
        <CircularProgress />
        <Stack alignItems="center" spacing={0.5}>
          {audioLoadingDisplay()}
          {audioLoading.retries > 0 && (
            <Typography variant="caption">
              Network Interruption. Retry {audioLoading.retries}
            </Typography>
          )}
        </Stack>
      </Stack>
      <WaitDialog open={isSaving} />
      <WaveformDisplay
        wavesurferReady={wavesurferReady}
        waveSurferHeight={waveSurferHeight}
        mainRef={mainRef}
        sampleRate={sampleRate}
        fftFrequencies={fftFrequencies}
        setFrequencyRange={setFrequencyRange}
        clip={clip}
        preferencesSave={preferencesSave}
        mode={mode}
      >
        {children}
      </WaveformDisplay>
      {wavesurferReady && (
        <>
          <FineTuneDialog
            fftSamples={fftSamples}
            maxFrequency={sampleRate / 2.0}
            zoom={[fftFrequencies.min, fftFrequencies.max]}
            onApply={fftApply}
            onCancel={fftCancel}
          />
          <AudioControls
            wavesurfer={wavesurfer}
            preferencesSave={preferencesSave}
            onNext={onNext}
            onPrevious={onPrevious}
          />
        </>
      )}
    </Stack>
  );
};
